import React from "react";
import { connect } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import { NotificationManager } from "react-notifications";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import "react-notifications/lib/notifications.css";
import { FormattedMessage, injectIntl } from "react-intl";
import Roles from "../../enums/Roles";
import PriceUtil from "../../util/PriceUtil";
import APIUrl from "../../APIUrl";
import { validateRental, searchEquipmentsByRef } from "../../actions/rentals/rentals";
import { getResidents, addResident } from "../../actions/residents/residents";
import {
  addFavoriteRental,
  deleteFavoriteRental,
} from "../../actions/user/user";
import UserGender from "../../enums/UserGender";
import Icon from "../sub/Icon";
import "../../css/rentals/RentalProductRow.css";

// Gender labels for display
const GenderLabels = {
  [UserGender.MISTER]: "Monsieur",
  [UserGender.MRS]: "Madame",
};

class RentalProductRow extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      startDate: "",
      endDate: "",
      isLightboxOpen: false,
      disableButton: false,
      showCommentModal: false,
      validationComment: "",
      quantity: 1,
      allocationResident: '',
      allocationCommun: '',
      // Resident selection state
      selectedResidents: [],
      showNewResidentForm: false,
      newResidentGender: '',
      newResidentName: '',
      newResidentFirstName: '',
      isAddingResident: false,
      // Location state for residents (each has: floor, room, fromTampon, tamponRefInterne, tamponEquipmentId)
      residentLocations: [],
      // Tampon search state (indexed by "resident-index", e.g., "resident-0", "resident-1")
      tamponSearchResults: {},
      tamponIsSearching: {},
      tamponRefInterneFocused: {},
    };
  }

  componentDidMount() {
    // Fetch residents on mount
    this.props.onGetResidents();
  }

  setFavorite(ref_frn) {
    let data = { ref_frn: ref_frn };
    let message;

    var successCallback = (msg) => {
      NotificationManager.success(msg);
    };

    if (!this.checkFavorite(ref_frn)) {
      message = this.props.intl.formatMessage({ id: "Favorite.Rental.Added" });
      this.props.onAddFavoriteRental(data, successCallback(message));
    } else {
      message = this.props.intl.formatMessage({ id: "Favorite.Rental.Removed" });
      this.props.onDeleteFavoriteRental(data, successCallback(message));
    }
  }

  checkFavorite(ref_frn) {
    if (this.props.user.favorite_rental_products) {
      for (let favorite of this.props.user.favorite_rental_products) {
        if (favorite.ref_frn === ref_frn) {
          return true;
        }
      }
    }
    return false;
  }

  validateRental(e) {
    e.preventDefault();

    // Show comment modal instead of directly validating
    this.setState({ showCommentModal: true });
  }

  closeCommentModal = () => {
    this.setState({
      showCommentModal: false,
      validationComment: "",
      quantity: 1,
      allocationResident: '',
      allocationCommun: '',
      selectedResidents: [],
      showNewResidentForm: false,
      newResidentGender: '',
      newResidentName: '',
      newResidentFirstName: '',
      residentLocations: [],
      tamponSearchResults: {},
      tamponIsSearching: {},
      tamponRefInterneFocused: {},
    });
  };

  handleAllocationResidentChange = (value) => {
    const newValue = value === '' ? '' : parseInt(value) || '';
    const currentCount = this.state.selectedResidents.length;

    this.setState({ allocationResident: newValue });

    // Adjust selected residents array and locations based on new count
    if (typeof newValue === 'number' && newValue !== currentCount) {
      if (newValue > currentCount) {
        // Add empty slots
        const newResidents = [...this.state.selectedResidents];
        const newLocations = [...this.state.residentLocations];
        for (let i = currentCount; i < newValue; i++) {
          newResidents.push('');
          newLocations.push({ floor: '', room: '', fromTampon: false, tamponRefInterne: '', tamponEquipmentId: null });
        }
        this.setState({ selectedResidents: newResidents, residentLocations: newLocations });
      } else if (newValue < currentCount) {
        // Remove excess slots
        this.setState({
          selectedResidents: this.state.selectedResidents.slice(0, newValue),
          residentLocations: this.state.residentLocations.slice(0, newValue),
        });
      }
    } else if (newValue === '' || newValue === 0) {
      this.setState({ selectedResidents: [], residentLocations: [] });
    }
  };

  // Gestion de la checkbox "Prendre dans le tampon" par location résident
  handleFromTamponChange = (index, checked) => {
    const newLocations = [...this.state.residentLocations];
    if (newLocations[index]) {
      newLocations[index] = {
        ...newLocations[index],
        fromTampon: checked,
        tamponRefInterne: checked ? newLocations[index].tamponRefInterne : '',
        tamponEquipmentId: checked ? newLocations[index].tamponEquipmentId : null,
      };
      this.setState({ residentLocations: newLocations });
    }
  };

  handleTamponRefInterneChange = (index, value) => {
    const searchTerm = value.toUpperCase();
    const key = `resident-${index}`;

    // Update the ref in the resident location array
    const newLocations = [...this.state.residentLocations];
    if (newLocations[index]) {
      newLocations[index] = { ...newLocations[index], tamponRefInterne: searchTerm, tamponEquipmentId: null };
      this.setState({ residentLocations: newLocations });
    }

    // Search for equipments by internal reference
    if (searchTerm && searchTerm.trim().length >= 1) {
      this.setState({ tamponIsSearching: { ...this.state.tamponIsSearching, [key]: true } });

      this.props.onSearchEquipmentsByRef(searchTerm).then((results) => {
        this.setState({
          tamponSearchResults: { ...this.state.tamponSearchResults, [key]: results || [] },
          tamponIsSearching: { ...this.state.tamponIsSearching, [key]: false },
        });
      }).catch((err) => {
        console.error("Error searching equipments:", err);
        this.setState({
          tamponSearchResults: { ...this.state.tamponSearchResults, [key]: [] },
          tamponIsSearching: { ...this.state.tamponIsSearching, [key]: false },
        });
      });
    } else {
      this.setState({ tamponSearchResults: { ...this.state.tamponSearchResults, [key]: [] } });
    }
  };

  handleTamponRefInterneSelect = (index, equipment) => {
    const key = `resident-${index}`;
    const newLocations = [...this.state.residentLocations];
    if (newLocations[index]) {
      newLocations[index] = { ...newLocations[index], tamponRefInterne: equipment.internal_ref, tamponEquipmentId: equipment._id };
      this.setState({
        residentLocations: newLocations,
        tamponRefInterneFocused: { ...this.state.tamponRefInterneFocused, [key]: false },
        tamponSearchResults: { ...this.state.tamponSearchResults, [key]: [] },
      });
    }
  };

  handleTamponRefInterneFocus = (index) => {
    const key = `resident-${index}`;
    this.setState({ tamponRefInterneFocused: { ...this.state.tamponRefInterneFocused, [key]: true } });
  };

  handleTamponRefInterneBlur = (index) => {
    const key = `resident-${index}`;
    // Delay to allow click on suggestion to register
    setTimeout(() => {
      this.setState({ tamponRefInterneFocused: { ...this.state.tamponRefInterneFocused, [key]: false } });
    }, 200);
  };

  handleTamponRefInterneClear = (index) => {
    const key = `resident-${index}`;
    const newLocations = [...this.state.residentLocations];
    if (newLocations[index]) {
      newLocations[index] = { ...newLocations[index], tamponRefInterne: '', tamponEquipmentId: null };
      this.setState({
        residentLocations: newLocations,
        tamponSearchResults: { ...this.state.tamponSearchResults, [key]: [] },
      });
    }
  };

  handleResidentLocationChange = (index, field, value) => {
    const newLocations = [...this.state.residentLocations];
    if (!newLocations[index]) {
      newLocations[index] = { floor: '', room: '' };
    }
    newLocations[index][field] = value;
    this.setState({ residentLocations: newLocations });
  };

  handleResidentSelection = (index, residentId) => {
    const newSelectedResidents = [...this.state.selectedResidents];
    newSelectedResidents[index] = residentId;
    this.setState({ selectedResidents: newSelectedResidents });
  };

  handleRemoveResident = (index) => {
    const newSelectedResidents = [...this.state.selectedResidents];
    newSelectedResidents.splice(index, 1);

    const newResidentLocations = [...this.state.residentLocations];
    newResidentLocations.splice(index, 1);

    // Also decrement the allocationResident count
    const newAllocationResident = Math.max(0, (parseInt(this.state.allocationResident) || 0) - 1);

    this.setState({
      selectedResidents: newSelectedResidents,
      residentLocations: newResidentLocations,
      allocationResident: newAllocationResident || '',
    });
  };

  toggleNewResidentForm = () => {
    this.setState({
      showNewResidentForm: !this.state.showNewResidentForm,
      newResidentGender: '',
      newResidentName: '',
      newResidentFirstName: '',
    });
  };

  handleAddNewResident = () => {
    const { newResidentName, newResidentFirstName, newResidentGender } = this.state;

    if (!newResidentName.trim() || !newResidentFirstName.trim()) {
      NotificationManager.error("Le nom et le prénom sont obligatoires", "Erreur", 5000);
      return;
    }

    this.setState({ isAddingResident: true });

    const normalizedName = newResidentName.trim().toUpperCase();
    const normalizedFirstName = newResidentFirstName.trim();

    const residentData = {
      name: normalizedName,
      first_name: normalizedFirstName,
      gender: newResidentGender || undefined,
    };

    this.props.onAddResident(residentData, () => {
      NotificationManager.success("Résident ajouté avec succès", "Succès", 3000);

      // Attendre un court instant que le state Redux soit mis à jour, puis trouver le nouveau résident
      setTimeout(() => {
        const userEstablishmentId = this.props.user.establishment_id;
        const newResident = this.props.residents.find(
          r => r.establishment_id === userEstablishmentId &&
               r.name === normalizedName &&
               r.first_name === normalizedFirstName
        );

        if (newResident) {
          // Trouver le premier slot vide dans selectedResidents
          const emptyIndex = this.state.selectedResidents.findIndex(id => !id);
          if (emptyIndex !== -1) {
            // Remplir le slot vide avec le nouveau résident
            const newSelectedResidents = [...this.state.selectedResidents];
            newSelectedResidents[emptyIndex] = newResident._id;
            this.setState({ selectedResidents: newSelectedResidents });
          } else if (this.state.selectedResidents.length < (parseInt(this.state.allocationResident) || 0)) {
            // Ajouter à la fin si on a de la place
            this.setState({
              selectedResidents: [...this.state.selectedResidents, newResident._id]
            });
          }
        }
      }, 100);

      this.setState({
        showNewResidentForm: false,
        newResidentGender: '',
        newResidentName: '',
        newResidentFirstName: '',
        isAddingResident: false,
      });
    }).catch((err) => {
      NotificationManager.error("Erreur lors de l'ajout du résident", "Erreur", 5000);
      this.setState({ isAddingResident: false });
    });
  };

  getAvailableResidents = (currentIndex) => {
    // Filter residents by user's establishment
    const userEstablishmentId = this.props.user.establishment_id;
    const establishmentResidents = this.props.residents.filter(
      r => r.establishment_id === userEstablishmentId
    );

    // Get already selected resident IDs (except current index)
    const selectedIds = this.state.selectedResidents.filter((id, idx) => idx !== currentIndex && id);

    // Return residents not already selected
    return establishmentResidents.filter(r => !selectedIds.includes(r._id));
  };

  getAllocationTotal = () => {
    return (parseInt(this.state.allocationResident) || 0) +
           (parseInt(this.state.allocationCommun) || 0);
  };

  isAllocationExceedingQuantity = () => {
    const allocationTotal = this.getAllocationTotal();
    return allocationTotal > (this.state.quantity || 0);
  };

  handleCommentChange = (e) => {
    this.setState({ validationComment: e.target.value });
  };

  confirmValidation = () => {
    // Validation de la quantité
    if (!this.state.quantity || this.state.quantity < 1) {
      NotificationManager.error(
        "La quantité doit être au minimum 1",
        "Erreur",
        5000,
      );
      return;
    }

    // Calcul de la répartition (peut être partielle)
    const allocationTotal = (parseInt(this.state.allocationResident) || 0) + (parseInt(this.state.allocationCommun) || 0);

    // Vérifier que la répartition ne dépasse pas la quantité
    if (allocationTotal > this.state.quantity) {
      NotificationManager.error(
        `La somme de la répartition (${allocationTotal}) ne peut pas dépasser la quantité (${this.state.quantity})`,
        "Erreur",
        5000,
      );
      return;
    }

    // Vérifier que chaque location résident avec "fromTampon" a une référence interne
    const residentsMissingRef = this.state.residentLocations.filter(loc => loc.fromTampon && !loc.tamponRefInterne?.trim());

    if (residentsMissingRef.length > 0) {
      NotificationManager.error(
        "La référence interne est obligatoire pour chaque location prise dans le tampon",
        "Erreur",
        5000,
      );
      return;
    }

    const allocationData = allocationTotal > 0 ? {
      resident: parseInt(this.state.allocationResident) || 0,
      commun: parseInt(this.state.allocationCommun) || 0,
    } : null;

    // Filter out empty resident selections
    const selectedResidentIds = this.state.selectedResidents.filter(id => id);

    // Build locations data for residents (each with their own tampon info)
    const locationsData = {
      residents: this.state.residentLocations,
    };

    this.setState({
      disableButton: true,
      showCommentModal: false,
    });

    this.props
      .onValidateRental(
        this.props.product._id,
        this.state.startDate,
        this.state.endDate,
        this.state.validationComment,
        this.state.quantity,
        allocationData,
        selectedResidentIds.length > 0 ? selectedResidentIds : null,
        locationsData,
      )
      .then(() => {
        // Vérifier si au moins une location a été prise dans le tampon
        const hasTamponLocations = this.state.residentLocations.some(loc => loc.fromTampon);

        NotificationManager.success(
          hasTamponLocations
            ? "Location validée avec succès! Une notification de réalimentation a été envoyée au PSDM."
            : "Location validée avec succès!",
          "Succès",
          3000,
        );
        this.setState({
          startDate: "",
          endDate: "",
          disableButton: false,
          validationComment: "",
          quantity: 1,
          allocationResident: '',
          allocationCommun: '',
          selectedResidents: [],
          residentLocations: [],
          tamponSearchResults: {},
          tamponIsSearching: {},
          tamponRefInterneFocused: {},
        });
      })
      .catch((error) => {
        NotificationManager.error(
          "Erreur lors de la validation de la location: " +
            (error.response?.data?.error || error.message),
          "Erreur",
          5000,
        );
        this.setState({ disableButton: false });
      });
  };

  render() {
    const product = this.props.product;
    const productFormat =
      product.formats && product.formats[0] ? product.formats[0] : {};

    // Product image handling - using same logic as ProductRow
    const forceCacheRefresh = this.props.imageRefreshTimestamp || Date.now();
    const refFrn = productFormat.ref_frn || "";
    const ref = productFormat.ref || "";

    const baseUrl =
      APIUrl.getProductImg +
      product.mercurial_id +
      "/" +
      refFrn +
      "/" +
      forceCacheRefresh +
      "/" +
      ref;

    const productImage = productFormat.parentProductRefFrn
      ? baseUrl +
        "/" +
        productFormat.parentProductRefFrn +
        "/?token=" +
        APIUrl.jwtToken
      : baseUrl + "/?token=" + APIUrl.jwtToken;

    let isHighlighted = false;
    if (
      this.props.searchProductId &&
      product._id === this.props.searchProductId
    ) {
      isHighlighted = true;
    }

    // Price display logic
    let priceHT = PriceUtil.priceHt(productFormat, 1, 2);
    let priceTTC = PriceUtil.priceTtc(productFormat, 1, 2);

    // Date inputs for rental period
    var dateInputs = (
      <td className="d-none d-md-table-cell col-3 align-middle rental-dates-container">
        <div className="rental-dates-wrapper">
          <div className="rental-date-input-group">
            <label className="text-muted rental-date-label">Date début *</label>
            <input
              type="date"
              className="form-control rental-date-input"
              value={this.state.startDate}
              onChange={(e) => this.setState({ startDate: e.target.value })}
              min={new Date().toISOString().split("T")[0]}
              required
            />
          </div>
          <div className="rental-date-input-group">
            <label className="text-muted rental-date-label">Date fin</label>
            <input
              type="date"
              className="form-control rental-date-input"
              value={this.state.endDate}
              onChange={(e) => this.setState({ endDate: e.target.value })}
              min={this.state.startDate}
              disabled={!this.state.startDate}
            />
          </div>
        </div>
      </td>
    );

    // Favorite button logic
    let favoriteBtnClass = "outline-success";
    let favoriteIconClass = "star";
    let favoriteMessage = <FormattedMessage id="Favorite.Rental.Add" />;

    if (this.checkFavorite(product.ref_frn)) {
      favoriteBtnClass = "success";
      favoriteIconClass = "star";
      favoriteMessage = <FormattedMessage id="Favorite.Rental.Remove" />;
    }

    var validateButton = (
      <td className="d-none d-md-table-cell col-2 text-center align-middle">
        <button
          className="btn rental-validate-button"
          onClick={(e) => this.validateRental(e)}
          disabled={!this.state.startDate || this.state.disableButton}
        >
          Valider location
        </button>
      </td>
    );

    // Bouton favori à afficher à gauche de l'image
    var favoriteButton = (
      <td className="text-center align-middle" style={{ width: "50px" }}>
        <Button
          variant={favoriteBtnClass}
          size="sm"
          onClick={() => this.setFavorite(product.ref_frn)}
          title={
            this.checkFavorite(product.ref_frn)
              ? this.props.intl.formatMessage({ id: "Favorite.Rental.Remove" })
              : this.props.intl.formatMessage({ id: "Favorite.Rental.Add" })
          }
        >
          <Icon icon={favoriteIconClass} />
        </Button>
      </td>
    );

    // Mobile version
    var dateInputsResponsive = (
      <td className="d-md-none col-2">
        <div className="d-flex flex-column">
          <input
            type="date"
            className="form-control form-control-sm mb-1"
            value={this.state.startDate}
            onChange={(e) => this.setState({ startDate: e.target.value })}
            min={new Date().toISOString().split("T")[0]}
            placeholder="Début"
            required
          />
          <input
            type="date"
            className="form-control form-control-sm"
            value={this.state.endDate}
            onChange={(e) => this.setState({ endDate: e.target.value })}
            min={this.state.startDate}
            disabled={!this.state.startDate}
            placeholder="Fin"
          />
        </div>
      </td>
    );

    // Admin/Sales Rep view - no inputs
    if (
      this.props.user.role === Roles.SALES_REP ||
      this.props.user.role === Roles.ADMIN
    ) {
      dateInputs = null;
      validateButton = null;
      dateInputsResponsive = null;
    }

    return (
      <>
        <tr
          className={`d-flex align-items-center rental-product-row ${isHighlighted ? "highlighted" : ""}`}
          id={`product-${product._id}`}
        >
          {favoriteButton}
          <td className="col-2 text-center align-middle">
            <div className="rental-product-image-container">
              <img
                alt={product.designation}
                src={productImage}
                className="rental-product-image"
                onClick={() => this.setState({ isLightboxOpen: true })}
              />
            </div>
            {this.state.isLightboxOpen && (
              <Lightbox
                mainSrc={productImage}
                onCloseRequest={() => this.setState({ isLightboxOpen: false })}
                imagePadding={50}
              />
            )}
          </td>
          <td className="col align-middle">
            <div className="rental-product-details">
              <h6 className="rental-product-title">{product.designation}</h6>
              {product.caracteristiques && (
                <div className="rental-product-characteristics">
                  {product.caracteristiques}
                </div>
              )}
              {product.sous_famille && (
                <div className="mt-2">
                  <span className="rental-product-badge">
                    {product.sous_famille}
                  </span>
                </div>
              )}
            </div>
          </td>
          <td className="d-none d-md-table-cell col-1 text-end align-middle">
            <div className="rental-product-price-ht">{priceHT} €</div>
          </td>
          <td className="d-none d-md-table-cell col-1 text-end align-middle">
            <div className="rental-product-price-ttc">{priceTTC} €</div>
          </td>
          {dateInputs}
          {validateButton}
          {dateInputsResponsive}
        </tr>

        {/* Comment Modal */}
        <Modal
          show={this.state.showCommentModal}
          onHide={this.closeCommentModal}
          backdrop={this.state.disableButton ? "static" : true}
          keyboard={!this.state.disableButton}
          centered
          size="md"
          className="rental-validation-modal"
        >
          <Modal.Header>
            <Modal.Title>
              <i className="fas fa-check-circle"></i>
              Validation de la location
            </Modal.Title>
            {!this.state.disableButton && (
              <button
                type="button"
                onClick={this.closeCommentModal}
                className="rental-validation-modal-close-btn"
              >
                <span>&times;</span>
              </button>
            )}
          </Modal.Header>

          <Modal.Body>
            <div style={{ marginBottom: "24px" }}>
              <div className="rental-validation-product-info">
                <div className="rental-validation-product-label">
                  Vous êtes sur le point de valider la location pour :
                </div>
                <div className="rental-validation-product-name">
                  {product.designation}
                </div>
              </div>

              {/* Quantité */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="quantity"
                  className="rental-validation-comment-label"
                >
                  Quantité *
                </label>
                <input
                  id="quantity"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={this.state.quantity}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    this.setState({ quantity: value === '' ? '' : parseInt(value) });
                  }}
                  disabled={this.state.disableButton}
                  className="rental-validation-textarea"
                  style={{ height: "56px" }}
                />
              </div>

              {/* Répartition */}
              <div style={{ marginBottom: "20px" }}>
                <label className="rental-validation-comment-label">
                  Répartition (optionnel)
                </label>
                <div style={{ fontSize: "0.875rem", color: "#6b7280", marginBottom: "8px" }}>
                  Vous pouvez spécifier comment cette quantité est répartie
                </div>
                <div style={{ display: "flex", gap: "12px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "0.875rem", marginBottom: "4px", display: "block" }}>
                      Résident
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={this.state.allocationResident}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        this.handleAllocationResidentChange(value);
                      }}
                      disabled={this.state.disableButton}
                      placeholder="0"
                      className="rental-validation-textarea"
                      style={{ height: "48px" }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: "0.875rem", marginBottom: "4px", display: "block" }}>
                      En commun
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={this.state.allocationCommun}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        this.setState({ allocationCommun: value === '' ? '' : parseInt(value) });
                      }}
                      disabled={this.state.disableButton}
                      placeholder="0"
                      className="rental-validation-textarea"
                      style={{ height: "48px" }}
                    />
                  </div>
                </div>

                {/* Resident Selection Fields */}
                {this.state.selectedResidents.length > 0 && (
                  <div className="resident-selection-container">
                    <div className="resident-selection-header">
                      <label className="resident-selection-label">
                        Assigner les résidents
                      </label>
                      <button
                        type="button"
                        className="resident-add-new-btn"
                        onClick={this.toggleNewResidentForm}
                        disabled={this.state.disableButton}
                      >
                        <i className="fas fa-plus"></i> Nouveau résident
                      </button>
                    </div>

                    {/* New Resident Form */}
                    {this.state.showNewResidentForm && (
                      <div className="new-resident-form">
                        <div className="new-resident-form-row">
                          <div className="new-resident-field" style={{ flex: 1 }}>
                            <label>Civilité</label>
                            <select
                              value={this.state.newResidentGender}
                              onChange={(e) => this.setState({ newResidentGender: e.target.value })}
                              disabled={this.state.isAddingResident}
                              className="rental-validation-textarea"
                              style={{ height: "48px" }}
                            >
                              <option value="">Civilité</option>
                              {Object.values(UserGender).map((value) => (
                                <option key={value} value={value}>{GenderLabels[value]}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="new-resident-form-row" style={{ marginTop: "12px" }}>
                          <div className="new-resident-field" style={{ flex: 1 }}>
                            <label>Nom *</label>
                            <input
                              type="text"
                              value={this.state.newResidentName}
                              onChange={(e) => this.setState({ newResidentName: e.target.value.toUpperCase() })}
                              disabled={this.state.isAddingResident}
                              placeholder="NOM"
                              className="rental-validation-textarea"
                              style={{ height: "48px" }}
                            />
                          </div>
                          <div className="new-resident-field" style={{ flex: 1 }}>
                            <label>Prénom *</label>
                            <input
                              type="text"
                              value={this.state.newResidentFirstName}
                              onChange={(e) => this.setState({ newResidentFirstName: e.target.value })}
                              disabled={this.state.isAddingResident}
                              placeholder="Prénom"
                              className="rental-validation-textarea"
                              style={{ height: "48px" }}
                            />
                          </div>
                          <div className="new-resident-actions">
                            <button
                              type="button"
                              className="new-resident-save-btn"
                              onClick={this.handleAddNewResident}
                              disabled={this.state.isAddingResident || !this.state.newResidentName.trim() || !this.state.newResidentFirstName.trim()}
                            >
                              {this.state.isAddingResident ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-check"></i>
                              )}
                            </button>
                            <button
                              type="button"
                              className="new-resident-cancel-btn"
                              onClick={this.toggleNewResidentForm}
                              disabled={this.state.isAddingResident}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Resident Selection Dropdowns */}
                    <div className="resident-selection-list">
                      {this.state.selectedResidents.map((selectedId, index) => {
                        const availableResidents = this.getAvailableResidents(index);
                        const location = this.state.residentLocations[index] || { floor: '', room: '', fromTampon: false, tamponRefInterne: '', tamponEquipmentId: null };
                        const tamponKey = `resident-${index}`;
                        const tamponSearchResults = this.state.tamponSearchResults[tamponKey] || [];
                        const tamponIsSearching = this.state.tamponIsSearching[tamponKey] || false;
                        const tamponRefInterneFocused = this.state.tamponRefInterneFocused[tamponKey] || false;
                        return (
                          <div key={index} className="resident-selection-item-wrapper">
                            <div className="resident-selection-item">
                              <span className="resident-selection-number">{index + 1}</span>
                              <select
                                value={selectedId}
                                onChange={(e) => this.handleResidentSelection(index, e.target.value)}
                                disabled={this.state.disableButton}
                                className="rental-validation-textarea resident-select"
                              >
                                <option value="">Sélectionner un résident</option>
                                {availableResidents.map(resident => (
                                  <option key={resident._id} value={resident._id}>
                                    {resident.name} {resident.first_name}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="resident-remove-btn"
                                onClick={() => this.handleRemoveResident(index)}
                                disabled={this.state.disableButton}
                                title="Retirer ce résident"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                            <div className="resident-location-row">
                              <input
                                type="text"
                                placeholder="Étage"
                                value={location.floor}
                                onChange={(e) => this.handleResidentLocationChange(index, 'floor', e.target.value)}
                                disabled={this.state.disableButton}
                                className="rental-validation-textarea location-input"
                              />
                              <input
                                type="text"
                                placeholder="Chambre"
                                value={location.room}
                                onChange={(e) => this.handleResidentLocationChange(index, 'room', e.target.value)}
                                disabled={this.state.disableButton}
                                className="rental-validation-textarea location-input"
                              />
                            </div>
                            {/* Tampon option per resident */}
                            <div className="tampon-option-row">
                              <label className="tampon-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={location.fromTampon}
                                  onChange={(e) => this.handleFromTamponChange(index, e.target.checked)}
                                  disabled={this.state.disableButton}
                                />
                                Prendre dans le tampon
                              </label>
                              {location.fromTampon && (
                                <div className="rental-ref-interne-input-wrapper tampon-ref-inline">
                                  <input
                                    type="text"
                                    value={location.tamponRefInterne}
                                    onChange={(e) => this.handleTamponRefInterneChange(index, e.target.value)}
                                    onFocus={() => this.handleTamponRefInterneFocus(index)}
                                    onBlur={() => this.handleTamponRefInterneBlur(index)}
                                    disabled={this.state.disableButton}
                                    placeholder="Référence interne..."
                                    className="rental-ref-interne-input"
                                  />
                                  {location.tamponRefInterne && (
                                    <button
                                      type="button"
                                      className="rental-ref-interne-clear-btn"
                                      onClick={() => this.handleTamponRefInterneClear(index)}
                                      disabled={this.state.disableButton}
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  )}
                                  <div className="rental-ref-interne-search-icon">
                                    <i className="fas fa-search"></i>
                                  </div>
                                  {tamponIsSearching && (
                                    <div className="rental-ref-interne-loading">
                                      <i className="fas fa-spinner fa-spin"></i>
                                    </div>
                                  )}
                                  {tamponRefInterneFocused && tamponSearchResults.length > 0 && (
                                    <div className="rental-ref-interne-suggestions">
                                      {tamponSearchResults.map((eq) => (
                                        <div
                                          key={eq._id}
                                          className="rental-ref-interne-suggestion-item"
                                          onMouseDown={() => this.handleTamponRefInterneSelect(index, eq)}
                                        >
                                          <span className="suggestion-ref">{eq.internal_ref}</span>
                                          <span className="suggestion-details">
                                            {eq.establishment?.name || "Libre"} - {eq.brand?.name || ""} {eq.model?.name || ""}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Info message if at least one resident uses tampon */}
                    {this.state.residentLocations.some(loc => loc.fromTampon) && (
                      <div className="tampon-info-message">
                        <i className="fas fa-info-circle"></i> Une notification sera automatiquement envoyée au PSDM pour réalimenter le tampon
                      </div>
                    )}
                  </div>
                )}

                {/* Message d'erreur si la répartition dépasse la quantité */}
                {this.isAllocationExceedingQuantity() && (
                  <div className="alert alert-danger" role="alert" style={{ marginTop: "12px", padding: "12px", borderRadius: "8px" }}>
                    <i className="fas fa-exclamation-circle"></i> La répartition ({this.getAllocationTotal()}) ne peut pas dépasser la quantité ({this.state.quantity})
                  </div>
                )}
              </div>

              <div>
                <label
                  htmlFor="validationComment"
                  className="rental-validation-comment-label"
                >
                  Commentaire (optionnel)
                </label>
                <div className="rental-validation-textarea-wrapper">
                  <textarea
                    id="validationComment"
                    rows="4"
                    placeholder="Ajoutez un commentaire sur cette validation..."
                    value={this.state.validationComment}
                    onChange={this.handleCommentChange}
                    maxLength={500}
                    disabled={this.state.disableButton}
                    className="rental-validation-textarea"
                  />
                  <small
                    className={`rental-validation-char-count ${
                      this.state.validationComment.length > 450
                        ? "warning"
                        : "normal"
                    }`}
                  >
                    {this.state.validationComment.length}/500 caractères
                  </small>
                </div>
                {this.state.validationComment.length >= 500 && (
                  <small className="rental-validation-char-limit-warning">
                    Limite de 500 caractères atteinte
                  </small>
                )}
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              onClick={this.closeCommentModal}
              disabled={this.state.disableButton}
              className="rental-validation-cancel-btn"
            >
              Annuler
            </button>
            <button
              onClick={this.confirmValidation}
              disabled={
                this.state.disableButton ||
                this.state.validationComment.length > 500 ||
                this.isAllocationExceedingQuantity()
              }
              className={`rental-validation-confirm-btn ${
                this.state.disableButton ||
                this.state.validationComment.length > 500 ||
                this.isAllocationExceedingQuantity()
                  ? "disabled"
                  : "enabled"
              }`}
            >
              Valider la location
            </button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
    residents: state.residents,
  };
};

const mapDispatchToProps = (dispatch) => ({
  onValidateRental: (productId, startDate, endDate, validationComment, quantity, allocation, selectedResidents, locationsData) =>
    dispatch(validateRental(productId, startDate, endDate, validationComment, quantity, allocation, selectedResidents, locationsData)),
  onGetResidents: () => dispatch(getResidents()),
  onAddResident: (data, successCallback) => dispatch(addResident(data, successCallback)),
  onSearchEquipmentsByRef: (searchTerm) => dispatch(searchEquipmentsByRef(searchTerm)),
  onAddFavoriteRental: (data, successCallback) =>
    dispatch(addFavoriteRental(data, successCallback)),
  onDeleteFavoriteRental: (data, successCallback) =>
    dispatch(deleteFavoriteRental(data, successCallback)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(RentalProductRow));
